import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import AlertBanner from "../feature/AlertBanner";
import { useTranslation } from "react-i18next";
import { useAdmin } from "../../contexts/AdminContext";

export default function Navbar() {
  const { settings } = useAdmin();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const navLinks = [
    { label: t("common.home"), path: "/" },
    { label: t("common.how_it_works"), path: "/comment" },
    { label: t("common.good_cause"), path: "/#guter-zweck" },
    { label: t("common.contact"), path: "/contact" },
  ];

  const mobileOnlyLinks = [
    { label: t("common.participate"), path: "/forfaits", highlight: true },
  ];

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path.split("#")[0]) && path.split("#")[0] !== "/";
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-slate-900/90 backdrop-blur-xl border-b border-white/5 transition-all duration-300 overflow-x-hidden">
      {/* Alert banner — appears after a few seconds */}
      <AlertBanner />

      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:h-24">

        {/* Logo + vertical divider */}
        <Link to="/" className="flex flex-row items-center gap-2 cursor-pointer shrink-0">
          <img
            src={settings.logo}
            alt={settings.siteName || "Traumhausverlosung"}
            className="h-7 w-auto object-contain lg:h-10"
          />
        </Link>
        
        {/* Desktop nav links */}
        <ul className="hidden lg:flex items-center justify-center gap-6 xl:gap-8 flex-1 px-8">
          {navLinks.map((link) => (
            <li key={link.label}>
              <Link
                to={link.path}
                className={`text-sm font-semibold whitespace-nowrap transition-all duration-300 cursor-pointer no-underline xl:text-base ${
                  isActive(link.path) ? "text-white" : "text-white/70 hover:text-white"
                }`}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>

        {/* Desktop right: login + language + CTA */}
        <div className="hidden lg:flex items-center gap-4 shrink-0">
          <Link
            to="/compte"
            className="flex h-10 w-10 items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer border border-white/10 rounded-full"
            title={t("common.account")}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-user-line text-lg"></i>
            </span>
          </Link>
          <Link
            to="/forfaits"
            className="inline-flex items-center justify-center rounded-full border-2 border-yellow-400 bg-yellow-400 px-6 py-2.5 text-sm font-bold text-slate-900 whitespace-nowrap cursor-pointer transition-colors duration-300 hover:border-yellow-300 hover:bg-yellow-300 xl:text-base"
          >
            {t("common.participate")}
          </Link>
        </div>

        {/* Mobile items (Login + Hamburger) */}
        <div className="lg:hidden flex items-center gap-4">
          <Link to="/compte" className="text-white/70 hover:text-white mt-1">
            <i className="ri-user-line text-xl"></i>
          </Link>
          <button
            className="flex h-10 w-10 items-center justify-center text-white cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menü öffnen"
            aria-expanded={mobileOpen}
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 bg-slate-900 border-t border-white/10 shadow-2xl ${
          mobileOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <ul className="flex flex-col px-6 py-4 space-y-1">
          {/* Participate Main Mobile CTA */}
          <li className="py-2">
            <Link
              to="/forfaits"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-2 bg-yellow-400 text-slate-900 font-extrabold py-3.5 rounded-xl no-underline transition-all duration-300"
            >
              {t("common.participate")}
              <i className="ri-ticket-2-fill text-lg"></i>
            </Link>
          </li>
          
          {/* Shared nav links */}
          {navLinks.map((link) => (
            <li key={link.label} className="py-2">
              <Link
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`block text-base font-semibold transition-colors cursor-pointer no-underline ${
                  isActive(link.path) ? "text-white" : "text-white/60 hover:text-white"
                }`}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </header>
  );
}
